import axios from 'axios';

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Shop owner dashboard (Frontend_Dashboard_User) — opened from the storefront
// header when the owner is logged in and viewing their own shop.
export const DASHBOARD_URL =
  process.env.REACT_APP_DASHBOARD_URL || 'https://funny-basbousa-fbb027.netlify.app/';

const api = axios.create({ baseURL: API_BASE });

// Normalize errors so UI code never receives an array/object as an error message.
// FastAPI returns 422 validation errors as `detail: [{type, loc, msg, input}, ...]`,
// which would otherwise crash React ("Objects are not valid as a React child").
function normalizeApiError(err) {
  const data = err?.response?.data;
  if (data && data.detail !== undefined && typeof data.detail !== 'string') {
    if (Array.isArray(data.detail)) {
      data.detail = data.detail
        .map((d) => (d && typeof d === 'object' && d.msg) || JSON.stringify(d))
        .join('; ');
    } else if (data.detail && typeof data.detail === 'object') {
      data.detail = data.detail.msg || JSON.stringify(data.detail);
    } else {
      data.detail = String(data.detail);
    }
  }
  return Promise.reject(err);
}

api.interceptors.response.use((res) => res, normalizeApiError);

export const fullUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

// Public endpoints
export const getShop = (username) => api.get(`/api/shops/${username}`).then((r) => r.data);
export const getProducts = (shopId, params = {}) =>
  api.get('/api/products/public', { params: { shop_id: shopId, ...params } }).then((r) => r.data);
export const getProduct = (id) => api.get(`/api/products/${id}/public`).then((r) => r.data);
export const getCategories = (shopId) =>
  api.get('/api/categories/public', { params: { shop_id: shopId } }).then((r) => r.data);
export const createOrder = (payload) => api.post('/api/orders', payload).then((r) => r.data);
export const createPayment = (payload) => api.post('/api/payments/aba/create', payload).then((r) => r.data);
export const trackOrder = (orderNumber) =>
  api.get('/api/orders/public/track', { params: { order_number: orderNumber } }).then((r) => r.data);

// Plans / self-serve shop creation
export const getPlans = () => api.get('/api/plans').then((r) => r.data);
export const registerShop = (payload) => api.post('/api/plans/register', payload).then((r) => r.data);
export const confirmPlan = (payload) => api.post('/api/plans/confirm', payload).then((r) => r.data);
export const ownerLogin = (payload) => api.post('/api/auth/login', payload).then((r) => r.data);
export const ownerCheck = (shopId, token) =>
  api.get(`/api/shops/${shopId}/owner`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data);
export const verifyPayment = (payload) => api.post('/api/payments/aba/verify', payload).then((r) => r.data);
export const customerHistory = (payload) => api.post('/api/orders/public/history', payload).then((r) => r.data);
export const customerSignup = (payload) => api.post('/api/customers/auth/signup', payload).then((r) => r.data);
export const customerSignin = (payload) => api.post('/api/customers/auth/signin', payload).then((r) => r.data);
export const getMyOrders = (token) =>
  api.get('/api/customers/auth/orders', { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.data);
export const createOrderAsCustomer = (payload, token) =>
  api.post('/api/orders', payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.data);

// Customer profile management
export const updateMyProfile = (token, data) =>
  api.put('/api/customers/auth/me', data, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.data);
export const changeMyPassword = (token, data) =>
  api.post('/api/customers/auth/change-password', data, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.data);

// Shop chatbot (owner Q&A + Google Gemini - shop-scoped answers only)
export const getChatbotConfig = (shopId) =>
  api.get('/api/chatbot/public', { params: { shop_id: shopId } }).then((r) => r.data);
export const askChatbot = (payload) => api.post('/api/chatbot/ask', payload).then((r) => r.data);

// Storefront sidebars (articles + videos)
export const getShopContent = (shopId) =>
  api.get('/api/content/public', { params: { shop_id: shopId } }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Mini Shop Community — the social platform (posts, likes, comments, shares,
// follows, notifications, chat, profiles, shops, admin moderation).
// Uses its own client so the community JWT never mixes with shop/customer auth.
// ---------------------------------------------------------------------------
const communityApi = axios.create({ baseURL: API_BASE });

communityApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('ms_community_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

communityApi.interceptors.response.use((res) => res, normalizeApiError);

export const communityToken = () => localStorage.getItem('ms_community_token');

// Auth
export const cSignup = (data) => communityApi.post('/api/community/auth/signup', data).then((r) => r.data);
export const cSignin = (data) => communityApi.post('/api/community/auth/signin', data).then((r) => r.data);
export const cMe = () => communityApi.get('/api/community/auth/me').then((r) => r.data);
export const cUpdateMe = (data) => communityApi.put('/api/community/auth/me', data).then((r) => r.data);
export const cChangePassword = (data) =>
  communityApi.post('/api/community/auth/change-password', data).then((r) => r.data);

// Login with Google — the only way in (no password forms on the web).
// `scope` decides which account is signed in: 'community' | 'customer' (needs
// shopId) | 'platform'. See Backend_API/routers/social_auth.py.
export const oauthConfig = () => api.get('/api/auth/oauth/config').then((r) => r.data);
export const customerMe = (token) =>
  api.get('/api/customers/auth/me', { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.data);
export const authMe = (token) =>
  api.get('/api/auth/me', { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.data);
/** Where the "Continue with Google" button sends the browser.
 *
 * `origin` is this website's own address: the API uses it for the Google
 * redirect URI, so the login works on whatever domain the site is served from
 * (`https://codekhmerlearningcommunity.site/_oauth/google` in production,
 * `http://localhost:3000/_oauth/google` locally) — the address must be listed in
 * the OAuth client's "Authorized redirect URIs".
 */
export const googleLoginUrl = ({ scope = 'community', shopId = null, next = '' } = {}) => {
  const params = new URLSearchParams({ scope });
  if (shopId) params.set('shop_id', String(shopId));
  if (next) params.set('next', next);
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `${API_BASE}/api/auth/google/start?${params.toString()}`;
};
export const cUpload = (files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return communityApi.post('/api/community/upload', fd).then((r) => r.data);
};
/**
 * Upload MP3 music / voice notes (≤ 10 MB). The backend compresses them
 * (`kind: 'music'` → 128 kbps stereo, `kind: 'voice'` → 64 kbps mono) and
 * returns `{ url, size, original_size, compressed, duration }`.
 */
export const cUploadAudio = (file, kind = 'music') => {
  const fd = new FormData();
  fd.append('file', file);
  return communityApi
    .post(`/api/community/upload/audio?kind=${kind}`, fd, { timeout: 180000 })
    .then((r) => r.data);
};
/**
 * Upload a video for a Facebook-like post / Reel (≤ 10 MB). The backend
 * compresses it to MP4 (max 720p) and returns
 * `{ url, thumb, size, original_size, compressed, duration, width, height, is_reel }`.
 */
export const cUploadVideo = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return communityApi
    .post('/api/community/upload/video', fd, { timeout: 300000 })
    .then((r) => r.data);
};

// Reels (TikTok-style vertical video feed; ?all=1 includes landscape videos too)
export const cReels = (params = {}) =>
  communityApi.get('/api/community/reels', { params }).then((r) => r.data);

// Categories + posts
export const cCategories = () => communityApi.get('/api/community/categories').then((r) => r.data);
// Community left sidebar ("General" menu built by the admin in Frontend_Admin)
export const cSidebar = () => communityApi.get('/api/community/sidebar').then((r) => r.data);
export const cFeed = (params = {}) => communityApi.get('/api/community/posts', { params }).then((r) => r.data);
/** Members leaderboard: metric = posts | likes | active | followers, period = all | month | week */
export const cLeaderboard = (params = {}) =>
  communityApi.get('/api/community/leaderboard', { params }).then((r) => r.data);

// Community shop — every product of every shop (filters: shop, category_id,
// category (name across shops), q, sort, page). Returns items + facets.
export const cProducts = (params = {}) =>
  communityApi.get('/api/community/products', { params }).then((r) => r.data);
/** One product's public detail (with its shop card + more from that shop). */
export const cProduct = (id) =>
  communityApi.get(`/api/community/product/${id}`).then((r) => r.data);
export const cPost = (id) => communityApi.get(`/api/community/posts/${id}`).then((r) => r.data);
export const cCreatePost = (data) => communityApi.post('/api/community/posts', data).then((r) => r.data);
export const cUpdatePost = (id, data) => communityApi.put(`/api/community/posts/${id}`, data).then((r) => r.data);
export const cDeletePost = (id) => communityApi.delete(`/api/community/posts/${id}`).then((r) => r.data);
export const cToggleLike = (id) => communityApi.post(`/api/community/posts/${id}/like`).then((r) => r.data);
// ★ Favourite: save / unsave an article + the list of saved articles
export const cToggleSave = (id) => communityApi.post(`/api/community/posts/${id}/save`).then((r) => r.data);
export const cSavedPosts = (params = {}) => communityApi.get('/api/community/saved', { params }).then((r) => r.data);
export const cShare = (id, channel) =>
  communityApi.post(`/api/community/posts/${id}/share`, { channel }).then((r) => r.data);
export const cComments = (id) => communityApi.get(`/api/community/posts/${id}/comments`).then((r) => r.data);
export const cAddComment = (id, data) =>
  communityApi.post(`/api/community/posts/${id}/comments`, data).then((r) => r.data);
export const cDeleteComment = (id) => communityApi.delete(`/api/community/comments/${id}`).then((r) => r.data);

// People
export const cUsers = (params = {}) => communityApi.get('/api/community/users', { params }).then((r) => r.data);
export const cUser = (id) => communityApi.get(`/api/community/users/${id}`).then((r) => r.data);
export const cUserPosts = (id, params = {}) =>
  communityApi.get(`/api/community/users/${id}/posts`, { params }).then((r) => r.data);
export const cToggleFollow = (id) => communityApi.post(`/api/community/users/${id}/follow`).then((r) => r.data);
export const cFollowers = (id) => communityApi.get(`/api/community/users/${id}/followers`).then((r) => r.data);
export const cFollowing = (id) => communityApi.get(`/api/community/users/${id}/following`).then((r) => r.data);

// Notifications + chat
export const cNotifications = (params = {}) =>
  communityApi.get('/api/community/notifications', { params }).then((r) => r.data);
export const cUnreadNotifications = () =>
  communityApi.get('/api/community/notifications/unread').then((r) => r.data);
export const cReadNotifications = (ids) =>
  communityApi.post('/api/community/notifications/read', ids ? { ids } : {}).then((r) => r.data);
export const cDeleteNotification = (id) =>
  communityApi.delete(`/api/community/notifications/${id}`).then((r) => r.data);
export const cUnreadMessages = () =>
  communityApi.get('/api/community/messages/unread').then((r) => r.data);
export const cConversations = () =>
  communityApi.get('/api/community/messages/conversations').then((r) => r.data);
export const cThread = (userId) =>
  communityApi.get(`/api/community/messages/${userId}`).then((r) => r.data);
export const cSendMessage = (data) => communityApi.post('/api/community/messages', data).then((r) => r.data);
export const cEditMessage = (id, data) =>
  communityApi.put(`/api/community/messages/${id}`, data).then((r) => r.data);
export const cDeleteMessage = (id) => communityApi.delete(`/api/community/messages/${id}`).then((r) => r.data);
export const cDeleteMessageForMe = (id) =>
  communityApi.delete(`/api/community/messages/${id}/me`).then((r) => r.data);

// Stories (Facebook-style, live for 24 hours)
export const cStories = () => communityApi.get('/api/community/stories').then((r) => r.data);
export const cCreateStory = (data) => communityApi.post('/api/community/stories', data).then((r) => r.data);
export const cViewStory = (id) => communityApi.post(`/api/community/stories/${id}/view`).then((r) => r.data);
export const cStoryViewers = (id) => communityApi.get(`/api/community/stories/${id}/views`).then((r) => r.data);
export const cDeleteStory = (id) => communityApi.delete(`/api/community/stories/${id}`).then((r) => r.data);

// Shops directory
export const cShops = (params = {}) => communityApi.get('/api/community/shops', { params }).then((r) => r.data);
export const cShop = (username) => communityApi.get(`/api/community/shops/${username}`).then((r) => r.data);

// ── Channels (max 3 per member) — create, subscribe (“ជាវ”), channel feed ──
export const cChannels = (params = {}) =>
  communityApi.get('/api/community/channels', { params }).then((r) => r.data);
export const cMyChannels = () =>
  communityApi.get('/api/community/channels/mine').then((r) => r.data);
export const cMySubscriptions = () =>
  communityApi.get('/api/community/channels/subscriptions').then((r) => r.data);
export const cCreateChannel = (data) =>
  communityApi.post('/api/community/channels', data).then((r) => r.data);
export const cChannel = (ref) =>
  communityApi.get(`/api/community/channels/${ref}`).then((r) => r.data);
export const cUpdateChannel = (ref, data) =>
  communityApi.put(`/api/community/channels/${ref}`, data).then((r) => r.data);
export const cDeleteChannel = (ref) =>
  communityApi.delete(`/api/community/channels/${ref}`).then((r) => r.data);
export const cSubscribeChannel = (ref) =>
  communityApi.post(`/api/community/channels/${ref}/subscribe`).then((r) => r.data);
export const cChannelSubscribers = (ref) =>
  communityApi.get(`/api/community/channels/${ref}/subscribers`).then((r) => r.data);
export const cSetSubscriberRole = (ref, userId, role) =>
  communityApi.post(`/api/community/channels/${ref}/subscribers/${userId}/role`, { role })
    .then((r) => r.data);
export const cRemoveSubscriber = (ref, userId) =>
  communityApi.delete(`/api/community/channels/${ref}/subscribers/${userId}`).then((r) => r.data);
export const cChannelPosts = (ref, params = {}) =>
  communityApi.get(`/api/community/channels/${ref}/posts`, { params }).then((r) => r.data);

// ── Groups — create, invite link, members/admins, nickname, logo, group chat ──
export const cGroups = (params = {}) =>
  communityApi.get('/api/community/groups', { params }).then((r) => r.data);
export const cMyGroups = () =>
  communityApi.get('/api/community/groups/mine').then((r) => r.data);
export const cDiscoverGroups = (params = {}) =>
  communityApi.get('/api/community/groups/discover', { params }).then((r) => r.data);
export const cCreateGroup = (data) =>
  communityApi.post('/api/community/groups', data).then((r) => r.data);
export const cGroup = (ref) =>
  communityApi.get(`/api/community/groups/${ref}`).then((r) => r.data);
export const cUpdateGroup = (ref, data) =>
  communityApi.put(`/api/community/groups/${ref}`, data).then((r) => r.data);
export const cDeleteGroup = (ref) =>
  communityApi.delete(`/api/community/groups/${ref}`).then((r) => r.data);
export const cResetGroupLink = (ref) =>
  communityApi.post(`/api/community/groups/${ref}/link/reset`).then((r) => r.data);
export const cGroupInvite = (code) =>
  communityApi.get(`/api/community/groups/invite/${code}`).then((r) => r.data);
export const cJoinGroupByCode = (code) =>
  communityApi.post(`/api/community/groups/join/${code}`).then((r) => r.data);
export const cJoinGroup = (ref) =>
  communityApi.post(`/api/community/groups/${ref}/join`).then((r) => r.data);
export const cLeaveGroup = (ref) =>
  communityApi.post(`/api/community/groups/${ref}/leave`).then((r) => r.data);
export const cGroupMembers = (ref, params = {}) =>
  communityApi.get(`/api/community/groups/${ref}/members`, { params }).then((r) => r.data);
export const cAddGroupMember = (ref, data) =>
  communityApi.post(`/api/community/groups/${ref}/members`, data).then((r) => r.data);
export const cUpdateGroupMember = (ref, userId, data) =>
  communityApi.put(`/api/community/groups/${ref}/members/${userId}`, data).then((r) => r.data);
export const cRemoveGroupMember = (ref, userId) =>
  communityApi.delete(`/api/community/groups/${ref}/members/${userId}`).then((r) => r.data);
export const cGroupMessages = (ref, params = {}) =>
  communityApi.get(`/api/community/groups/${ref}/messages`, { params }).then((r) => r.data);
export const cSendGroupMessage = (ref, data) =>
  communityApi.post(`/api/community/groups/${ref}/messages`, data).then((r) => r.data);
export const cEditGroupMessage = (ref, id, data) =>
  communityApi.put(`/api/community/groups/${ref}/messages/${id}`, data).then((r) => r.data);
export const cDeleteGroupMessage = (ref, id, forMe = false) =>
  communityApi.delete(`/api/community/groups/${ref}/messages/${id}${forMe ? '?for_me=1' : ''}`)
    .then((r) => r.data);

// ── Blocks (“គណនីដែលត្រូវបានប្លុក”) + the ✓ verification ──
export const cBlocked = () => communityApi.get('/api/community/blocks').then((r) => r.data);
export const cBlockUser = (id, reason = '') =>
  communityApi.post(`/api/community/blocks/${id}`, { reason }).then((r) => r.data);
export const cUnblockUser = (id) =>
  communityApi.delete(`/api/community/blocks/${id}`).then((r) => r.data);
export const cVerifyCriteria = () =>
  communityApi.get('/api/community/verify/criteria').then((r) => r.data);
export const cVerifyMe = () => communityApi.get('/api/community/verify/me').then((r) => r.data);
export const cApplyVerify = (note = '') =>
  communityApi.post('/api/community/verify/request', { note }).then((r) => r.data);

export default api;
